import { RefObject, useEffect, useRef, useState } from 'react';

interface RangeScrubberOptions {
  playerRef?: RefObject<HTMLMediaElement>;
  resetKey: string | null;
  knownDuration: number | null;
  initialInSec?: number;
  initialOutSec?: number | null;
}

// Shared iPhone-style range playback for both the source clip browser and the
// selected timeline-clip editor. The range is UI state only; callers decide
// whether a button saves a new clip or applies the range as a timeline trim.
export function useRangeScrubber({
  playerRef,
  resetKey,
  knownDuration,
  initialInSec = 0,
  initialOutSec = null,
}: RangeScrubberOptions) {
  const [mediaDuration, setMediaDuration] = useState<number | null>(knownDuration);
  const [inSec, setInSec] = useState(initialInSec);
  const [outSec, setOutSec] = useState<number | null>(initialOutSec ?? knownDuration);
  const [playheadSec, setPlayheadSec] = useState(initialInSec);
  const rangeRef = useRef({ inSec: initialInSec, outSec: initialOutSec ?? knownDuration ?? Infinity });
  rangeRef.current = { inSec, outSec: outSec ?? Infinity };

  useEffect(() => {
    const start = Math.max(0, Math.min(initialInSec, Math.max(0, (knownDuration ?? Infinity) - 0.2)));
    const requestedEnd = initialOutSec ?? knownDuration;
    const end = requestedEnd == null ? null : Math.max(start, Math.min(requestedEnd, knownDuration ?? requestedEnd));
    setMediaDuration(knownDuration);
    setInSec(start);
    setOutSec(end);
    setPlayheadSec(start);
    const el = playerRef?.current;
    if (el) { try { el.currentTime = start; } catch { /* not seekable yet */ } }
    // All initial values belong to resetKey; changing clips is the reset boundary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  // Keep the playhead marker live and loop playback inside the selected range.
  useEffect(() => {
    let raf: number;
    let lastShown = -1;
    const tick = () => {
      const el = playerRef?.current;
      if (el) {
        const range = rangeRef.current;
        if (!el.paused && isFinite(range.outSec) && range.outSec > range.inSec + 0.15) {
          if (el.currentTime >= range.outSec - 0.02 || el.currentTime < range.inSec - 0.05) {
            try { el.currentTime = range.inSec; } catch { /* not seekable yet */ }
          }
        }
        if (Math.abs(el.currentTime - lastShown) > 0.08) {
          lastShown = el.currentTime;
          setPlayheadSec(el.currentTime);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playerRef, resetKey]);

  const loadDuration = (duration: number) => {
    if (!isFinite(duration) || duration <= 0) return;
    setMediaDuration(duration);
    setInSec((value) => Math.min(value, Math.max(0, duration - 0.2)));
    setOutSec((value) => (value == null ? duration : Math.min(value, duration)));
    const start = Math.min(rangeRef.current.inSec, Math.max(0, duration - 0.2));
    const el = playerRef?.current;
    if (el) { try { el.currentTime = start; } catch { /* not seekable yet */ } }
  };

  const changeRange = (start: number, end: number, movedThumb: 0 | 1) => {
    setInSec(start);
    setOutSec(end);
    const el = playerRef?.current;
    if (el) { try { el.currentTime = movedThumb === 0 ? start : end; } catch { /* not seekable yet */ } }
  };

  const seek = (timeSec: number) => {
    const time = Math.max(0, Math.min(timeSec, mediaDuration ?? timeSec));
    const el = playerRef?.current;
    if (el) { try { el.currentTime = time; } catch { /* not seekable yet */ } }
    setPlayheadSec(time);
  };

  return { mediaDuration, inSec, outSec, playheadSec, loadDuration, changeRange, seek };
}
